import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, AdminSchool } from '../api';
import { Layout, Loading, ErrorBox } from '../components/Layout';

const STATUS_STYLE: Record<string, string> = {
  ACTIVE: 'bg-green-50 text-green-700 border-green-200',
  SUSPENDED: 'bg-amber-50 text-amber-700 border-amber-200',
  EXPIRED: 'bg-red-50 text-red-700 border-red-200',
};

function licenceLabel(iso: string | null): string {
  if (!iso) return 'No expiry';
  const d = new Date(iso);
  const expired = d.getTime() < Date.now();
  return `${d.toISOString().slice(0, 10)}${expired ? ' (expired)' : ''}`;
}

export function AdminSchools() {
  const [schools, setSchools] = useState<AdminSchool[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api.admin
      .schools()
      .then(setSchools)
      .catch((e: Error) => setError(e.message));
  }, []);

  return (
    <Layout>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-semibold text-slate-900">Schools</h1>
        <div className="flex items-center gap-3">
          <Link
            to="/admin/usage"
            className="text-sm px-3 py-2 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
          >
            Usage
          </Link>
          <Link
            to="/admin/schools/new"
            className="text-sm px-3 py-2 rounded-md bg-slate-900 text-white hover:bg-slate-800"
          >
            New school
          </Link>
        </div>
      </div>

      {error && <ErrorBox message={error} />}
      {!schools && !error && <Loading />}

      {schools && schools.length === 0 && (
        <p className="text-slate-500">No schools yet.</p>
      )}

      {schools && schools.length > 0 && (
        <div className="overflow-x-auto border border-slate-200 rounded-md bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left font-medium px-3 py-2">Name</th>
                <th className="text-left font-medium px-3 py-2">Status</th>
                <th className="text-left font-medium px-3 py-2">Seats</th>
                <th className="text-left font-medium px-3 py-2">Licence ends</th>
                <th className="text-left font-medium px-3 py-2">Entitlements</th>
              </tr>
            </thead>
            <tbody>
              {schools.map((s) => (
                <tr key={s.id} className="border-t border-slate-100">
                  <td className="px-3 py-2">
                    <Link
                      to={`/admin/schools/${s.id}`}
                      className="text-slate-900 font-medium hover:underline"
                    >
                      {s.name}
                    </Link>
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={
                        'inline-block px-2 py-0.5 rounded border text-xs ' +
                        (STATUS_STYLE[s.status] ?? '')
                      }
                    >
                      {s.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-slate-700">
                    {s.activeUsers}
                    {s.seatLimit != null ? ` / ${s.seatLimit}` : ' / ∞'}
                  </td>
                  <td className="px-3 py-2 text-slate-700">{licenceLabel(s.licenseEnd)}</td>
                  <td className="px-3 py-2 text-slate-700">
                    {s._count.entitlements === 0 ? (
                      // Worth calling out: no rows means the school sees nothing
                      // at all, which looks identical to a broken catalog.
                      <span className="text-amber-700">none — sees nothing</span>
                    ) : (
                      `${s._count.entitlements} grant(s)`
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Layout>
  );
}
